import env from 'lib/config/public-env'
import {snapBoundsToNearestPixels} from 'lib/grids/web-mercator-extents'

/**
 * Logo
 */
export const LOGO_URL = '/img/conveyal-128x128.png'
export const CB_HEX = '#2389c9'
export const CB_DARK = '#185c86'
export const CB_RGB = {r: 35, g: 137, b: 201}

/**
 * Maximum upload sizes
 */
const mbToBytes = (b: number): number => b * 1024 * 1024
// Java API
// Dictated by the reverse proxy used on the server.
export const SERVER_MAX_FILE_SIZE_BYTES = mbToBytes(500)
export const SERVER_NGINX_MAX_CLIENT_BODY_SIZE = mbToBytes(1000)
// JavaScript API
// https://vercel.com/docs/platform/limits?query=limit#serverless-function-payload-size-limit
export const VERCEL_MAX_FILE_SIZE_BYTES = mbToBytes(5)

/**
 * API URLs
 */
export const API_URL = `${env.API_URL}/api`

export const API = {
  Activity: `${API_URL}/activity`,
  AggregationArea: `${API_URL}/aggregationArea`,
  Analysis: `${API_URL}/analysis`,
  Bundle: `${API_URL}/bundle`,
  Cluster: `${API_URL}/cluster`,
  DataSource: `${API_URL}/dataSource`,
  GTFS: `${API_URL}/gtfs`,
  Jobs: `${API_URL}/jobs`,
  Opportunities: `${API_URL}/opportunities`,
  Regional: `${API_URL}/regional`,
  Workers: `${API_URL}/workers`
}
export const ANALYSIS_URL = `${API_URL}/analysis`

/**
 * Pages
 */
export const Pages = {
  aggregationAreas: '/regions/[regionId]/aggregationAreas',
  activity: '/regions/[regionId]/activity',
  changelog: '/changelog',
  dataSource: '/regions/[regionId]/dataSources/[dataSourceId]',
  dataSources: '/regions/[regionId]/dataSources',
  dataSourceUpload: '/regions/[regionId]/dataSources/upload',
  regions: '/',
  regionCreate: '/regions/create',
  regionSettings: '/regions/[regionId]/edit',
  projects: '/regions/[regionId]',
  projectCreate: '/regions/[regionId]/create-project',
  projectSettings: '/regions/[regionId]/projects/[projectId]/edit',
  projectSelect: '/regions/[regionId]/projects',
  report: '/report',
  bundle: '/regions/[regionId]/bundles/[bundleId]',
  bundles: '/regions/[regionId]/bundles',
  bundleCreate: '/regions/[regionId]/bundles/create',
  opportunities: '/regions/[regionId]/opportunities',
  opportunitiesUpload: '/regions/[regionId]/opportunities/upload',
  importShapefile: '/regions/[regionId]/projects/[projectId]/import-shapefile',
  modificationEdit:
    '/regions/[regionId]/projects/[projectId]/modifications/[modificationId]',
  modificationImport:
    '/regions/[regionId]/projects/[projectId]/import-modifications',
  modifications: '/regions/[regionId]/projects/[projectId]/modifications',
  analysis: '/regions/[regionId]/analysis',
  regionalAnalyses: '/regions/[regionId]/regional',
  regionalAnalysis: '/regions/[regionId]/regional/[analysisId]',
  login: '/api/auth/login',
  logout: '/api/auth/logout',
  authCallback: '/api/auth/callback'
}
export type PageKey = keyof typeof Pages

/**
 * Local Storage Keys
 */
export const LOCAL_STORAGE_KEY_MODIFICATIONS_ON_MAP = 'modifications-on-map'

/**
 * Line Editing and display
 */
export const NEW_LINE_WEIGHT = 3
export const HIGHLIGHTED_LINE_WEIGHT = NEW_LINE_WEIGHT * 2
export const NEW_STOP_RADIUS = 2.5

/**
 * Modification types
 */
export const ADD_STREETS = 'add-streets'
export const ADD_TRIP_PATTERN = 'add-trip-pattern'
export const ADJUST_DWELL_TIME = 'adjust-dwell-time'
export const ADJUST_SPEED = 'adjust-speed'
export const CONVERT_TO_FREQUENCY = 'convert-to-frequency'
export const CUSTOM_MODIFICATION = 'custom'
export const MODIFY_STREETS = 'modify-streets'
export const REMOVE_STOPS = 'remove-stops'
export const REMOVE_TRIPS = 'remove-trips'
export const REROUTE = 'reroute'
export const MODIFICATION_TYPES: CL.ModificationTypes[] = [
  ADD_STREETS,
  ADD_TRIP_PATTERN,
  ADJUST_DWELL_TIME,
  ADJUST_SPEED,
  CONVERT_TO_FREQUENCY,
  MODIFY_STREETS,
  REMOVE_STOPS,
  REMOVE_TRIPS,
  REROUTE,
  CUSTOM_MODIFICATION
]

/**
 * Transit Modes
 */
export const BUS = 'BUS'
export const RAIL = 'RAIL'
export const TRAM = 'TRAM'
export const SUBWAY = 'SUBWAY'
export const FERRY = 'FERRY'
export const CABLE_CAR = 'CABLE_CAR'
export const GONDOLA = 'GONDOLA'
export const FUNICULAR = 'FUNICULAR'
// R5 maps TROLLEYBUS to BUS and MONORAIL TO TRAM
// R5 supports AIR, but we do not support it here

// The mode index corresponds to the GTFS route_type integer
export const COMMON_TRANSIT_MODES: CL.TransitMode[] = [TRAM, SUBWAY, RAIL, BUS]

export const LESS_COMMON_TRANSIT_MODES: CL.TransitMode[] = [
  FERRY,
  CABLE_CAR,
  GONDOLA,
  FUNICULAR
]

export const ALL_TRANSIT_MODES = COMMON_TRANSIT_MODES.concat(
  LESS_COMMON_TRANSIT_MODES
)

/**
 * Street modes
 */
export const BICYCLE = 'BICYCLE'
export const BICYCLE_RENT = 'BICYCLE_RENT'
export const CAR = 'CAR'
export const CAR_PARK = 'CAR_PARK'
export const WALK = 'WALK'
export const ALL_STREET_MODES: CL.StreetMode[] = [BICYCLE, CAR, WALK]
export const EXTENDED_STREET_MODES: CL.ExtendedStreetMode[] = [
  ...ALL_STREET_MODES,
  BICYCLE_RENT,
  CAR_PARK
]

/**
 * Default values
 */
export const DEFAULT_ADD_STOPS_DWELL = 0
export const DEFAULT_ADJUST_DWELL_TIME_VALUE = 30
export const DEFAULT_ADJUST_SPEED_SCALE = 1
export const DEFAULT_STOP_SPACING_METERS = 400
export const DEFAULT_ZOOM_LEVEL = 9
export const MINIMUM_SNAP_STOP_ZOOM_LEVEL = 12
export const MINIMUM_STOP_SPACING = 50
export const MAX_ALLOWED_ORIGIN_POINTS = 5_000_000
export const SUPPORTED_ZOOM_LEVELS = [9, 10, 11] as const

// Default region bounds: Washington DC
const ne = {lat: 39.02345, lon: -76.81503}
const sw = {lat: 38.77764, lon: -77.25723}
export const DEFAULT_BOUNDS: CL.WgsBounds = snapBoundsToNearestPixels(
  {
    north: ne.lat,
    east: ne.lon,
    south: sw.lat,
    west: sw.lon
  },
  DEFAULT_ZOOM_LEVEL
)

/**
 * Percentiles of travel time to request from the backend. This is for
 * TRAVEL_TIME_SURFACE requests.
 */
export const TRAVEL_TIME_PERCENTILES = [5, 25, 50, 75, 95]

/**
 * No modifications & All modifications scenarios
 */
export const BASELINE_SCENARIO_ID = 'baseline'
export const BASELINE_SCENARIO = {
  _id: BASELINE_SCENARIO_ID,
  modificationIds: [],
  name: 'No modifications (baseline)'
} as unknown as CL.Scenario
export const ALL_MODIFICATIONS_SCENARIO_ID = 'all'
export const ALL_MODIFICATIONS_SCENARIO = {
  _id: ALL_MODIFICATIONS_SCENARIO_ID,
  modificationIds: [], // ID `all` indicates for the server to retrieve all modifications.
  name: 'All modifications'
} as unknown as CL.Scenario

const fontPath = '/fonts/inter-var-latin.woff2'
export const FONT_URL =
  env.VERCEL_ENV === 'production'
    ? `https://assets.conveyal.com${fontPath}`
    : fontPath

// Empty array is used as a default value so we can compare using `===`.
export const EMPTY_ARRAY = []
